import { useState, useRef, useContext, useEffect } from "react";
import { ChartContext } from "../../Context/ChartContext/ChartContext";
import MainContext from "../../Context/MainContext/MainContext";
import Tangerine from "../../Asset/Font/TangerineRegular.ttf";
import MUIGlobalStyles from "@mui/material/GlobalStyles";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIButton from "@mui/material/Button";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITypography from "@mui/material/Typography";
import MUIGrid from "@mui/material/Grid";
import MUIToggleButton from "@mui/material/ToggleButton";
import MUIToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import MUITextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";

import Dialog from "../DialogFunctional";
import SignaturePad from "../SignaturePad";

export default function DialogManageSignature({ open, onClose, onConfirm }) {
  const mainContext = useContext(MainContext);
  const theme = useTheme();

  const signatureRef = useRef(null);
  const initialRef = useRef(null);

  const signatureMethodTermTypes = Object.values(
    mainContext.termsIndexed,
  ).filter((term) => term.type === "signature_method");

  const [signatureTypeToggle, setSignatureTypeToggle] = useState("draw");
  const [signatureMethodTermType, setSignatureMethodTermType] = useState(
    signatureMethodTermTypes.find((term) => term.name === "Drawn").id,
  );

  const [signatureObject, setSignatureObject] = useState(null);
  const [initialObject, setInitialObject] = useState(null);

  /**
   * Reset the signature and initial objects when the dialog is closed
   * and clear the signature and initial references
   * and reset the signature type toggle to draw
   */
  useEffect(() => {
    if (!open) {
      signatureRef.current?.clear();
      initialRef.current?.clear();
      setSignatureObject(null);
      setInitialObject(null);
      setSignatureTypeToggle("draw");
    }
  }, [open]);

  /**
   * Create a new signature object in the chart context
   */
  useEffect(() => {
    if (open) {
      const signatureObject = {
        userprofileId: mainContext.currentUser.id,
        signatoryTermId: 135, //Hardcode to employee for now
        signatureTypeTermId: 130, //Hardcode to signature
        signatureMethodTermId: signatureMethodTermType
          ? signatureMethodTermType
          : null,
        // createdAt: new Date().toISOString(),
      };
      setSignatureObject(signatureObject);
    }
  }, [open]);

  /**
   * Create a new initial object in the chart context
   */
  useEffect(() => {
    if (open) {
      const initialObject = {
        userprofileId: mainContext.currentUser.id,
        signatoryTermId: 135, //Hardcode to employee for now
        signatureTypeTermId: 131, //Hardcode to initial
        signatureMethodTermId: signatureMethodTermType
          ? signatureMethodTermType
          : null,
        // createdAt: new Date().toISOString(),
      };
      setInitialObject(initialObject);
    }
  }, [open]);

  /**
   * Render header title of dialog
   */
  function renderHeader() {
    return (
      <MUITypography variant="h6" color="textPrimary">
        Manage Signature
      </MUITypography>
    );
  }

  /**
   * Render draw SignaturePad Component
   * @param {string} type - The type of signature
   * @returns {JSX.Element}
   */
  function renderDrawSignatureComponent(type) {
    return (
      <>
        <SignaturePad
          label={type === "Signature" ? "Signature" : "Initial"}
          ref={type === "Signature" ? signatureRef : initialRef}
          style={{ borderBottom: "1px solid #8f8f9d" }}
          options={{ penColor: theme.palette.primary.main }}
          onChange={() => {
            const svgString =
              type === "Signature"
                ? signatureRef.current?.getSignatureSVG()
                : initialRef.current?.getSignatureSVG();

            const blackSvgString = svgString
              ? svgString.replace(/stroke="[^"]*"/g, 'stroke="#000000"')
              : null;
            if (type === "Signature") {
              setSignatureObject((prev) => ({ ...prev, data: blackSvgString }));
            } else {
              setInitialObject((prev) => ({ ...prev, data: blackSvgString }));
            }
          }}
        />
        <MUIButton
          variant="text"
          color="error"
          size="small"
          sx={{ padding: "0px !important", alignSelf: "flex-start", mt: 1 }}
          onClick={() => {
            if (type === "Signature") {
              signatureRef.current.clear();
              setSignatureObject((prev) => ({ ...prev, data: null }));
            } else {
              initialRef.current.clear();
              setInitialObject((prev) => ({ ...prev, data: null }));
            }
          }}
        >
          Clear Signature
        </MUIButton>
      </>
    );
  }

  /**
   * Render type signature component
   * @param {string} type - The type of signature
   * @returns {JSX.Element}
   */
  function renderTypeSignatureComponent(type) {
    return (
      <>
        <MUIGlobalStyles
          styles={{
            "@font-face": {
              fontFamily: "Tangerine",
              src: `url(${Tangerine}) format('truetype')`,
              fontWeight: "normal",
              fontStyle: "normal",
            },
          }}
        />
        <MUITextField
          size="small"
          label={type}
          variant="standard"
          fullWidth={true}
          inputProps={{
            maxLength: 255,
          }}
          sx={{
            "& .MuiInputBase-input": {
              fontFamily: "Tangerine",
              fontSize: 36,
              padding: 0,
            },
          }}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => {
            if (type === "Signature") {
              setSignatureObject((prev) => ({
                ...prev,
                data: e.target.value ? e.target.value : null,
              }));
            } else {
              setInitialObject((prev) => ({
                ...prev,
                data: e.target.value ? e.target.value : null,
              }));
            }
          }}
        />
      </>
    );
  }

  /**
   * Render main content text of dialog
   */
  function renderContent() {
    const handleChange = (event, newAlignment) => {
      setSignatureTypeToggle(newAlignment);
      setSignatureObject((prev) => ({ ...prev, data: null }));
      setInitialObject((prev) => ({ ...prev, data: null }));
      signatureRef.current?.clear();
      initialRef.current?.clear();
    };

    return (
      <>
        <MUIToggleButtonGroup
          color="primary"
          size="small"
          exclusive
          value={signatureTypeToggle}
          onChange={(event, newValue) => {
            if (newValue !== null) {
              handleChange(event, newValue);
            }
          }}
          sx={{
            mb: 3,
            "& .MuiToggleButton-root": {
              border: "none",
              borderBottom: "2px solid transparent",
              paddingBottom: "2px",
              "&.Mui-selected": {
                backgroundColor: "transparent",
                borderBottom: "2px solid",
                borderBottomColor: "primary.main",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              },
            },
          }}
        >
          <MUIToggleButton
            value="draw"
            onClick={() => {
              if (signatureTypeToggle === "draw") {
                return;
              }
              const signatureMethodTermId = signatureMethodTermTypes.find(
                (term) => term.name === "Drawn",
              ).id;
              setSignatureMethodTermType(signatureMethodTermId);
              setSignatureObject({
                ...signatureObject,
                signatureMethodTermId: signatureMethodTermId
                  ? signatureMethodTermId
                  : null,
                data: null,
              });
              setInitialObject({
                ...initialObject,
                signatureMethodTermId: signatureMethodTermId
                  ? signatureMethodTermId
                  : null,
                data: null,
              });
            }}
          >
            Draw
          </MUIToggleButton>
          <MUIToggleButton
            value="type"
            onClick={() => {
              if (signatureTypeToggle === "type") {
                return;
              }
              const signatureMethodTermId = signatureMethodTermTypes.find(
                (term) => term.name === "Typed",
              ).id;
              setSignatureMethodTermType(signatureMethodTermId);
              setSignatureObject({
                ...signatureObject,
                signatureMethodTermId: signatureMethodTermId
                  ? signatureMethodTermId
                  : null,
                data: null,
              });
              setInitialObject({
                ...initialObject,
                signatureMethodTermId: signatureMethodTermId
                  ? signatureMethodTermId
                  : null,
                data: null,
              });
            }}
          >
            Type
          </MUIToggleButton>
        </MUIToggleButtonGroup>
        <MUIGrid container spacing={3} rowSpacing={0}>
          <MUIGrid item xs={12} lg={8}>
            {signatureTypeToggle === "draw" &&
              renderDrawSignatureComponent("Signature")}
            {signatureTypeToggle === "type" &&
              renderTypeSignatureComponent("Signature")}
          </MUIGrid>
          <MUIGrid item xs={12} lg={4}>
            {signatureTypeToggle === "draw" &&
              renderDrawSignatureComponent("Intial")}
            {signatureTypeToggle === "type" &&
              renderTypeSignatureComponent("Intial")}
          </MUIGrid>
        </MUIGrid>
      </>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  function renderActions() {
    return (
      <MUIDialogActions spacing={2}>
        <MUIButton
          variant="text"
          color="error"
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </MUIButton>
        <MUILoadingButton
          variant="contained"
          color="primary"
          disabled={!signatureObject?.data || !initialObject?.data}
          onClick={() => {
            onConfirm(signatureObject, initialObject);
          }}
        >
          Save
        </MUILoadingButton>
      </MUIDialogActions>
    );
  }

  /**
   * @returns {string} The max width of the dialog.
   */
  function getMaxWidth() {
    return "md";
  }

  /**
   * Render dialog component
   * @returns {JSX.Element}
   */
  return (
    <Dialog
      renderHeader={renderHeader}
      renderContent={renderContent}
      renderActions={renderActions}
      maxWidth={getMaxWidth}
      open={open}
      onClose={() => {
        onClose();
      }}
    />
  );
}
